/**
 * Custom hook for SearchBar autocomplete functionality
 *
 * Tiered strategy:
 * 1. Empty input: show recent search history
 * 2. Short input (1–2 chars): local history only
 * 3. Longer input (3+ chars): one debounced `/api/geocode` call (MOTIS via backend); no automatic second request.
 *    Optional Google fallback runs only when the user explicitly triggers `searchFallback`.
 *
 * @module useSearchBarAutocomplete
 */

import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { ItineraryService } from "../services/ItineraryService";
import { runOnTimeoutIfNotQueued } from "../tools/DeferredRunTools";
import { deduplicateHistory, filterSearchHistory } from "../tools/SearchInputs";
import { GeocodeSource, SearchResultItem } from "../types/geocode-ui.types";
import { type SearchHistoryItem } from "../types/GlobalContext";

interface UseSearchBarAutocompleteOptions {

    /** Setter for autocomplete values */
    "setAutocompleteValues": Dispatch<SetStateAction<SearchResultItem[]>>;

    /** Search history */
    "searchHistory": SearchHistoryItem[];

    /** Function to get coordinates (map center when available, else user position) */
    "getCoordinates": () => {
        "lat": number;
        "lon": number;
    };

    /** Reference to results container for scrolling */
    "resultsRef": React.RefObject<HTMLDivElement>;
}

interface UseSearchBarAutocompleteReturn {

    /** Whether to show autocomplete dropdown */
    "showAutocomplete": boolean;

    /** Setter for show autocomplete */
    "setShowAutocomplete": Dispatch<SetStateAction<boolean>>;

    /** Whether to show fallback search option */
    "showCompleteFromFallback": boolean;

    /** Setter for needToCompleteFromFallback */
    "setShowCompleteFromFallback": Dispatch<SetStateAction<boolean>>;

    /** Function to get suggestions based on input */
    "getSuggestions": ( query: string ) => void;

    /** Function to search using fallback provider */
    "searchFallback": ( query: string, isExplicit?: boolean ) => Promise<void>;

    /** Function to close autocomplete */
    "closeAutocomplete": () => void;
}

/** Itinerary service instance for API calls */
const itineraryService = new ItineraryService();

/** Debounce delay for autocomplete API calls (in milliseconds) */
const AUTOCOMPLETE_DEBOUNCE_MS = 500;

/**
 * Hook for SearchBar autocomplete functionality
 * 
 * @param {UseSearchBarAutocompleteOptions} options - Hook options
 * @returns {UseSearchBarAutocompleteReturn} Autocomplete state and functions
 */
export const useSearchBarAutocomplete = ( {
    setAutocompleteValues,
    searchHistory,
    getCoordinates,
    resultsRef,
}: UseSearchBarAutocompleteOptions ): UseSearchBarAutocompleteReturn => {
    const [ showAutocomplete, setShowAutocomplete ] = useState<boolean>( false );
    const [ showCompleteFromFallback, setShowCompleteFromFallback ] = useState<boolean>( true );
    const geocodeAbortControllerRef = useRef<AbortController | null>( null );
    const fallbackAbortControllerRef = useRef<AbortController | null>( null );
    const geocodeRequestSequenceRef = useRef<number>( 0 );
    const fallbackRequestSequenceRef = useRef<number>( 0 );

    const abortGeocodeRequest = useCallback( () => {
        if ( geocodeAbortControllerRef.current !== null ) {
            geocodeAbortControllerRef.current.abort();
            geocodeAbortControllerRef.current = null;
        }
    }, [] );

    const abortFallbackRequest = useCallback( () => {
        if ( fallbackAbortControllerRef.current !== null ) {
            fallbackAbortControllerRef.current.abort();
            fallbackAbortControllerRef.current = null;
        }
    }, [] );

    /**
     * Handle empty input: show recent history
     */
    const handleEmptyInput = useCallback(
        ( query: string ) => {
            geocodeRequestSequenceRef.current += 1;
            fallbackRequestSequenceRef.current += 1;
            abortGeocodeRequest();
            abortFallbackRequest();

            // filterSearchHistory expects isSource: true for departure, false for arrival
            const latestThree = filterSearchHistory( query, searchHistory );
            setAutocompleteValues( [ ...latestThree ] );
            setShowAutocomplete( true );

            /*
             * For both departure and arrival, hide Google search button when input is empty
             */
            setShowCompleteFromFallback( false );
        },
        [ abortFallbackRequest, abortGeocodeRequest, searchHistory, setAutocompleteValues ],
    );

    /**
     * Handle short input (1-2 chars): search local history only.
     */
    const handleShortInput = useCallback(
        ( query: string ) => {
            geocodeRequestSequenceRef.current += 1;
            fallbackRequestSequenceRef.current += 1;
            abortGeocodeRequest();
            abortFallbackRequest();

            // filterSearchHistory expects isSource: true for departure, false for arrival
            const findInLocal = filterSearchHistory( query, searchHistory );
            setAutocompleteValues( [ ...findInLocal ] );
            setShowAutocomplete( true );
            setShowCompleteFromFallback( false );
        },
        [ abortFallbackRequest, abortGeocodeRequest, searchHistory, setAutocompleteValues ],
    );

    /**
     * Search using fallback provider (Google). Only called when the user asks for it in the UI.
     *
     * @param query - Search query
     * @param isExplicit - True when the user clicked the fallback action (hides the button after)
     */
    const searchFallback = useCallback( async( query: string, isExplicit: boolean = false ) => {
        try {
            if ( query.length > 3 ) {
                const sequence = fallbackRequestSequenceRef.current + 1;
                fallbackRequestSequenceRef.current = sequence;
                abortFallbackRequest();
                const abortController = new AbortController();
                fallbackAbortControllerRef.current = abortController;
                const coords = getCoordinates();

                const resp = await itineraryService.geocodeFallback( {
                    "text":  query,
                    "place": {
                        "lat": coords.lat,
                        "lon": coords.lon,
                    },
                }, abortController.signal );
                if (
                    abortController.signal.aborted ||
                    sequence !== fallbackRequestSequenceRef.current
                ) {
                    return;
                }

                // Use functional update to avoid dependency on autoComplete
                setAutocompleteValues( ( prev ) => {
                    return deduplicateHistory( [ ...prev, ...resp.data ] );
                } );

                /*
                 * Only hide button if it was an explicit user click
                 * (for both departure and arrival)
                 */
                if ( isExplicit ) {
                    setShowCompleteFromFallback( false );
                }

                // Scroll to bottom to show new results
                if ( resultsRef.current != null ) {
                    resultsRef.current.scrollTop = resultsRef.current.scrollHeight;
                }
            }
        } catch( error: unknown ) {
            if ( error instanceof DOMException && error.name === "AbortError" ) {
                return;
            }
            setShowCompleteFromFallback( true );
        }
    }, [ abortFallbackRequest, getCoordinates, setAutocompleteValues, resultsRef ] );

    /**
     * Handle long input (3+ chars): query geocoding API with debounce
     */
    const handleLongInput = useCallback(
        ( query: string ) => {
            const sequence = geocodeRequestSequenceRef.current + 1;
            geocodeRequestSequenceRef.current = sequence;
            abortGeocodeRequest();
            runOnTimeoutIfNotQueued(
                "autoComplete",
                () => {
                    if ( sequence !== geocodeRequestSequenceRef.current ) {
                        return;
                    }
                    const abortController = new AbortController();
                    geocodeAbortControllerRef.current = abortController;
                    const coords = getCoordinates();

                    itineraryService.
                        geocode( {
                            "text":  query,
                            "place": {
                                "lat": coords.lat,
                                "lon": coords.lon,
                            },
                        }, abortController.signal ).
                        then( ( res ) => {
                            if (
                                abortController.signal.aborted ||
                                sequence !== geocodeRequestSequenceRef.current
                            ) {
                                return;
                            }

                            /*
                             * Filter local history first
                             * filterSearchHistory expects isSource: true for departure, false for arrival
                             */
                            const findInLocal = filterSearchHistory( query, searchHistory );

                            // Get geocoding results
                            const geocodeResults = res.data;

                            const combinedResults = deduplicateHistory( [ ...findInLocal, ...geocodeResults ] );

                            setAutocompleteValues( combinedResults );
                            setShowAutocomplete( true );

                            /*
                             * Keep explicit Google fallback available even when MOTIS returns results.
                             * Hide it only when Google results are already present.
                             */
                            const hasGoogleResults = combinedResults.some( ( item ) => item.source === GeocodeSource.GOOGLE );
                            setShowCompleteFromFallback( query.length >= 3 && !hasGoogleResults );
                        } ).
                        catch( ( err: unknown ) => {
                            if ( err instanceof DOMException && err.name === "AbortError" ) {
                                return;
                            }
                            if ( sequence !== geocodeRequestSequenceRef.current ) {
                                return;
                            }
                            console.error( err );

                            const findInLocal = filterSearchHistory( query, searchHistory );
                            setAutocompleteValues( [ ...findInLocal ] );
                            setShowAutocomplete( true );
                            setShowCompleteFromFallback( query.length >= 3 );
                        } );
                },
                AUTOCOMPLETE_DEBOUNCE_MS,
            );
        },
        [ abortGeocodeRequest, getCoordinates, searchHistory, setAutocompleteValues ],
    );

    /**
     * Get suggestions based on input length (see module doc).
     */
    const getSuggestions = useCallback(
        ( query: string ) => {
            if ( query.length === 0 ) {
                handleEmptyInput( query );

                return;
            }

            if ( query.length <= 2 ) {
                handleShortInput( query );

                return;
            }

            handleLongInput( query );
        },
        [ handleEmptyInput, handleShortInput, handleLongInput ],
    );

    /**
     * Close autocomplete dropdown
     */
    const closeAutocomplete = useCallback( () => {
        geocodeRequestSequenceRef.current += 1;
        fallbackRequestSequenceRef.current += 1;
        abortGeocodeRequest();
        abortFallbackRequest();
        setAutocompleteValues( [] );
        setShowAutocomplete( false );
    }, [ abortFallbackRequest, abortGeocodeRequest, setAutocompleteValues ] );

    return {
        showAutocomplete,
        setShowAutocomplete,
        showCompleteFromFallback,
        setShowCompleteFromFallback,
        getSuggestions,
        searchFallback,
        closeAutocomplete,
    };
};

